'use client'

import { Button } from '~/components/UI/shadcn/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '~/components/UI/shadcn/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '~/components/UI/shadcn/popover'
import ChevronDown from '../Dropdown/icon/chevron-down'
import Close from '../Input/icon/close'
import { useRef, useState } from 'react'
import { Label } from '~/components/UI/shadcn/label'
import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'

import { useQuery } from '@tanstack/react-query'
import { getFilters } from '~/components/Content/Filters/data'
import { parseAsString, useQueryState } from 'nuqs'

type ComboBox = {
  data: {
    // id: string
    label?: string
    required: boolean
    note?: string
  }
  items?: {
    label?: string
    id?: string
  }
  style?: 'Default' | 'Outline' | 'Minimal' | null | undefined
  disabled?: boolean | false
  queryKey?: string
}

const cbStyle = cva('bg-white border-2', {
  variants: {
    style: {
      Default: 'border-transparent disabled:bg-grey-m disabled:text-blue-d-80',
      Outline: 'border-blue-d-40 disabled:bg-grey-m disabled:text-blue-d-80',
      Minimal:
        'border-b-2 border-blue-d-40 border-x-0 border-t-0 disabled:bg-white disabled:placeholder:text-grey-m disabled:text-grey-m disabled:border-grey-m'
    }
  },
  defaultVariants: {
    style: 'Default'
  }
})

const ComboBox = ({ data, items, style, disabled, queryKey }: ComboBox) => {
  const [open, setOpen] = useState(false)
  // const [value, setValue] = useState('')
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement | null>(null)

  const { id } = items || {}
  const [query, setQuery] = useQueryState<string>(
    queryKey || 'filters',
    parseAsString
  )
  const normalisedQuery = query || ''

  const { data: filterData, isLoading } = useQuery({
    queryKey: [id],
    queryFn: () => getFilters(id || ''),
    enabled: !!id // Only fetch if id exists
  })

  if (isLoading) {
    return <div>Loading...</div>
  }

  return (
    <div className='gap-xs flex flex-col'>
      {data?.label && (
        <Label
          // htmlFor={data.id}
          className='flex justify-between'
        >
          {data.label}{' '}
          {data?.required && <span className='text-blue-m'>*</span>}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            role='combobox'
            aria-expanded={open}
            className={cn(
              cbStyle({ style }),
              'lg:text-base-l! gap-xs p-xs text-base-sm! hover:border-blue-d-80 sm:text-base-md! lg:p-xs-plus data-[state=open]:border-blue-d-80 h-auto justify-between rounded-none transition-colors hover:bg-transparent disabled:opacity-100'
            )}
            aria-label={
              normalisedQuery
                ? (filterData &&
                    'items' in filterData &&
                    filterData.items.find(item => item.id === normalisedQuery)
                      ?.label) ||
                  undefined
                : 'Select an option'
            }
            disabled={disabled}
          >
            {normalisedQuery
              ? filterData &&
                'items' in filterData &&
                filterData.items.find(item => item.id === normalisedQuery)
                  ?.label
              : 'Select an option'}
            <span className='shrink-0'>
              <ChevronDown
                fill={''}
                width={'1.25rem'}
                height={'1.25rem'}
                title={''}
              />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className='-mt-2xs w-[var(--radix-popover-trigger-width)] p-0'>
          <Command>
            <div className='px-xs py-3xs lg:py-2xs flex'>
              <CommandInput
                ref={inputRef}
                placeholder='Search...'
                value={inputValue}
                onValueChange={value => {
                  setInputValue(value)
                }}
              />
              {inputValue && (
                <button
                  onClick={() => {
                    setInputValue('')
                    inputRef.current?.focus()
                  }}
                  onKeyUp={e => {
                    if (e.key === 'Enter') {
                      setInputValue('')
                      inputRef.current?.focus()
                    }
                  }}
                >
                  <Close
                    fill={'#0E1647'}
                    width={'1rem'}
                    height={'1rem'}
                    title={'clear search'}
                  />
                </button>
              )}
            </div>
            <CommandList>
              <CommandEmpty>No option found.</CommandEmpty>
              <CommandGroup className='max-h-[300px] overflow-y-auto'>
                {filterData &&
                  'items' in filterData &&
                  Array.isArray(filterData.items) &&
                  filterData.items.map(item => (
                    <CommandItem
                      key={item.id}
                      value={item.id}
                      onSelect={currentValue => {
                        setQuery(
                          currentValue === normalisedQuery ? '' : currentValue
                        )
                        // setValue(currentValue === value ? '' : currentValue)
                        setOpen(false)
                      }}
                      className='px-xs lg:px-xs-plus py-xs cursor-pointer hover:underline'
                    >
                      {item.label}
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {data?.note && <p className='text-xs'>{data?.note}</p>}
    </div>
  )
}

export default ComboBox
